/**
 * Status mapping utility for Hebrew labels and order status management
 * Provides consistent status display across the application
 * 
 * Status flow: pending -> processing -> (ready_for_pickup OR shipped) -> completed
 * Alternative: pending -> cancelled
 */

export type OrderStatus = 'pending' | 'processing' | 'ready_for_pickup' | 'shipped' | 'cancelled' | 'completed';

/**
 * Maps order status enum values to Hebrew display labels
 */
export const getStatusLabel = (status: OrderStatus): string => {
  const statusLabels: Record<OrderStatus, string> = {
    pending: 'ממתין לתשלום',
    processing: 'בטיפול',
    ready_for_pickup: 'מוכן לאיסוף',
    shipped: 'נשלח',
    cancelled: 'בוטל',
    completed: 'הושלם'
  };
  
  return statusLabels[status] || status;
};

/**
 * Maps order status to appropriate badge variant
 */
export const getStatusVariant = (status: OrderStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'pending':
      return 'outline';
    case 'processing':
      return 'default';
    case 'ready_for_pickup':
    case 'shipped':
      return 'secondary';
    case 'completed':
      return 'default';
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
};

/**
 * Maps payment callback status to order status
 */
export const mapPaymentStatusToOrderStatus = (paymentStatus: string): OrderStatus => {
  switch (paymentStatus?.toLowerCase()) {
    case 'approved':
    case 'success':
    case 'completed':
      return 'processing'; // Hebrew: בטיפול
    case 'pending':
      return 'pending'; // Hebrew: ממתין לתשלום
    case 'failed':
    case 'cancelled':
    case 'declined':
      return 'cancelled'; // Hebrew: בוטל
    default:
      return 'pending';
  }
};

/**
 * Checks if order status allows payment retry
 */
export const canRetryPayment = (status: OrderStatus): boolean => {
  return status === 'pending' || status === 'cancelled';
};

/**
 * Gets user-friendly status description
 */
export const getStatusDescription = (status: OrderStatus): string => {
  const descriptions: Record<OrderStatus, string> = {
    pending: 'ההזמנה ממתינה לתשלום',
    processing: 'ההזמנה מעובדת כעת',
    ready_for_pickup: 'ההזמנה מוכנה לאיסוף',
    shipped: 'ההזמנה נשלחה אליך',
    cancelled: 'ההזמנה בוטלה',
    completed: 'ההזמנה הושלמה'
  };
  
  return descriptions[status] || 'סטטוס לא ידוע';
};

/**
 * Gets the color for each order status
 */
export const getStatusColor = (status: OrderStatus): string => {
  const colorMap: Record<OrderStatus, string> = {
    pending: '#6B7280',        // Gray
    processing: '#22C55E',     // Green
    ready_for_pickup: '#F59E0B', // Amber
    shipped: '#0EA5E9',        // Blue
    completed: '#059669',      // Emerald
    cancelled: '#DC2626'       // Red
  };
  
  return colorMap[status] || '#6B7280';
};
